async function loadSnarkjs() {
    const snarkjs = (await import("snarkjs"));
    return snarkjs;
}
const loaders = {};
/** Register a per-circuit loader. Idempotent. */
export function registerProofArtifactLoader(circuit, loader) {
    loaders[circuit] = loader;
}
async function getLoader(circuit) {
    if (loaders[circuit])
        return loaders[circuit];
    try {
        // Magic comments tell Vite/webpack NOT to statically bundle this
        // import — `proof-node-loader.ts` pulls in `node:fs` which can't run
        // in the browser. The dApp registers its own loaders BEFORE calling
        // generateProof, so this fallback never executes there.
        // Non-literal path so TS doesn't statically include the file in the
        // ESM build, which intentionally excludes src/node/**.
        // .js extension required by Node ESM strict-resolution (vitest /
        // raw `node` consumers). See poseidon.ts for the matching pattern.
        const nodeLoaderPath = "./node/proof-loader.js";
        const m = (await import(
        /* @vite-ignore */ /* webpackIgnore: true */ nodeLoaderPath));
        m.registerNodeProofDefaults();
    }
    catch (e) {
        throw new Error(`No proof artifact loader registered for "${circuit}". In a browser/Worker, ` +
            `call \`registerProofArtifactLoader("${circuit}", { loadWasm, loadZkey, loadVkey })\` ` +
            `BEFORE the first \`generateProof\` / \`generateMergeProof\` call. ` +
            `(Underlying error trying to lazy-load Node defaults: ${e.message})`);
    }
    if (!loaders[circuit]) {
        throw new Error(`Node defaults loaded but no loader registered for circuit "${circuit}". ` +
            `This indicates an internal misconfiguration in proof-node-loader.`);
    }
    return loaders[circuit];
}
export async function generateProof(inputs) {
    const loader = await getLoader("withdraw");
    const [wasm, zkey] = await Promise.all([loader.loadWasm(), loader.loadZkey()]);
    const snarkjs = await loadSnarkjs();
    const circuitInput = serialiseInputs(inputs);
    return snarkjs.groth16.fullProve(circuitInput, wasm, zkey);
}
export async function generateMergeProof(inputs) {
    const loader = await getLoader("merkleUpdate");
    const [wasm, zkey] = await Promise.all([loader.loadWasm(), loader.loadZkey()]);
    const snarkjs = await loadSnarkjs();
    const circuitInput = {
        prevRoot: inputs.prevRoot.toString(),
        newRoot: inputs.newRoot.toString(),
        commitment: inputs.commitment.toString(),
        leafIndex: inputs.leafIndex.toString(),
        pathElements: inputs.pathElements.map((x) => x.toString()),
        pathIndices: inputs.pathIndices.map((x) => x.toString()),
    };
    return snarkjs.groth16.fullProve(circuitInput, wasm, zkey);
}
export async function generateAmClaimProof(inputs) {
    const loader = await getLoader("amClaim");
    const [wasm, zkey] = await Promise.all([loader.loadWasm(), loader.loadZkey()]);
    const snarkjs = await loadSnarkjs();
    const circuitInput = {
        root: inputs.root.toString(),
        amNullifierHash: inputs.amNullifierHash.toString(),
        recipient: inputs.recipient.toString(),
        relayer: inputs.relayer.toString(),
        fee: inputs.fee.toString(),
        refund: inputs.refund.toString(),
        nullifier: inputs.nullifier.toString(),
        secret: inputs.secret.toString(),
        pathElements: inputs.pathElements.map((x) => x.toString()),
        pathIndices: inputs.pathIndices.map((x) => x.toString()),
    };
    return snarkjs.groth16.fullProve(circuitInput, wasm, zkey);
}
export async function verifyProof(proof, publicSignals) {
    const loader = await getLoader("withdraw");
    if (!loader.loadVkey) {
        throw new Error(`The "withdraw" loader does not provide a \`loadVkey\` function. ` +
            `verifyProof requires the verification key JSON. Re-register with a loader ` +
            `that includes \`loadVkey\`.`);
    }
    const vkey = await loader.loadVkey();
    const snarkjs = await loadSnarkjs();
    return snarkjs.groth16.verify(vkey, publicSignals, proof);
}
function serialiseInputs(i) {
    return {
        // Public signals (order matches `withdraw.circom`'s `signal input` declarations).
        root: i.root.toString(),
        nullifierHash: i.nullifierHash.toString(),
        recipient: i.recipient.toString(),
        relayer: i.relayer.toString(),
        fee: i.fee.toString(),
        refund: i.refund.toString(),
        // Private signals.
        nullifier: i.nullifier.toString(),
        secret: i.secret.toString(),
        pathElements: i.pathElements.map((x) => x.toString()),
        pathIndices: i.pathIndices.map((x) => x.toString()),
    };
}
//# sourceMappingURL=proof.js.map